/**
 * RatifyRoute — the sibling page.
 *
 * Ratification is NOT a grant: nothing persists, no counterparty receives
 * anything, and the verb is "do" rather than "may". It uses four of the
 * thirteen components — C1, C6, C10, C11 — which is why it lives here rather
 * than as a mode of the grant page. Forcing it in is what grew the old page
 * four whole-page escape hatches.
 *
 * Two modes, genuinely different acts:
 *   ratify           change a node's STRATUM to declared, so apps can no
 *                    longer modify it. Its content does not change.
 *   review_proposal  apply proposed CONTENT changes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import GrantPage, { GrantFrame } from '../../components/grant/GrantPage';
import { Explanation, Item, Terms, Decide } from '../../components/grant/parts';
import {
  proposalToDiff, ratifyTerms, reviewTerms, labelsFor, stratumChange,
} from '../authorization/ratify';
import './GrantRoute.css';

export default function RatifyRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const proposalId = params.get('proposal_id');
  const mode = params.get('action') === 'review_proposal' ? 'review_proposal' : 'ratify';

  const [proposal, setProposal] = useState(null);
  const [node, setNode] = useState(null);
  const [error, setError] = useState(proposalId ? null : 'No proposal named');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!proposalId) return;
    let alive = true;
    api.getProposal(proposalId)
      .then((r) => {
        if (!alive) return;
        const p = r?.proposal || r;
        setProposal(p);
        const nid = p?.canonical_data?.node_id || (p?.target_node_ids || [])[0];
        if (nid) api.getNode?.(nid).then((n) => alive && setNode(n?.node || n)).catch(() => {});
      })
      .catch((e) => alive && setError(e?.message || 'Could not load this proposal'));
    return () => { alive = false; };
  }, [proposalId]);

  const onConfirm = useCallback(async () => {
    setBusy(true);
    try {
      await api.ratifyProposal(proposal.id);
      navigate('/proposals?done=1');
    } catch (e) {
      setError(e?.message || 'Could not apply this');
      setBusy(false);
    }
  }, [proposal, navigate]);

  const diff = useMemo(
    () => proposalToDiff(proposal?.canonical_data?.changes, node?.value_json),
    [proposal, node],
  );

  if (error) {
    return (
      <GrantFrame className="gp-route">
        <div className="gp-route--error" role="alert">{error}</div>
      </GrantFrame>
    );
  }
  if (!proposal) return <GrantFrame className="gp-route" data-testid="loading" aria-busy="true" />;

  const proposedBy = proposal.app_name || proposal.created_by_app_name || null;
  const labels = labelsFor(mode);
  const move = stratumChange(node);

  const slots = {
    C1: (
      <Explanation
        headline={mode === 'ratify'
          ? <>Declare <b>{proposal.title || 'this entry'}</b> as yours?</>
          : <>Apply a change to <b>{proposal.title || 'your graph'}</b>?</>}
        tile="·"
        name={proposedBy ? `Proposed by ${proposedBy}` : 'A proposal'}
        sub={mode === 'ratify'
          ? (move ? `Currently ${move.from}. Nothing about its content changes.`
                  : 'Already declared.')
          : 'Nothing is granted by applying this.'}
      />
    ),
    C6: mode === 'review_proposal' && diff.length
      ? <Item diff={diff.map((d) => ({ label: d.field, value: `${d.from ?? '—'} → ${d.to}` }))} />
      : <Item title={proposal.title || 'This entry'}
              snippet={proposal.description}
              meta={node?.stratum ? `stratum: ${node.stratum}` : undefined} />,
    C10: <Terms rows={mode === 'ratify'
      ? ratifyTerms(node, { proposedBy })
      : reviewTerms({ proposedBy })} />,
    C11: (
      <Decide confirmLabel={busy ? 'Working…' : labels.confirm}
              denyLabel={labels.deny} disabled={busy}
              onConfirm={onConfirm} onDeny={() => navigate(-1)} />
    ),
  };

  return <GrantPage edge="u->r" slots={slots} className="gp-route" />;
}
